import React, { useState } from 'react'
import { googlePayment, dataCollector } from 'braintree-web'
import { useBraintree } from './use-braintree';
import { useBasketStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore';
import { GooglePayArgs, PaymentMethodSetupData } from '../types/payments';
import BonusCardApp from '../utils/app';
import { CARD_TYPES, CHECKOUT_ERROR_MESSAGES, PAYMENT_METHOD_IDS } from '../utils/constants';
import { useBraintreeThreeDS } from './use-braintree-threeDS';
import { useBasketReview } from './use-basket-review';

export function useBraintreeGooglePay() {
  const { createBraintreeClientInstance } = useBraintree()
  const { shippingAddress } = useBasketStore()
  const { sfccEnv, googlePayMerchantId, googlePayMerchantName, currency } = useGlobalStore()
  const [hasGooglePayButtonRendered, setHasGooglePayButtonRendered] = useState<boolean>(false)
  const orderStore = useOrderStore()
  const { handleThreeDSAuthentication } = useBraintreeThreeDS()
  const { reviewBasket } = useBasketReview()

  const initGooglePayScript = () => {
    const elem = document.getElementById('googlePayScript')
    if (!elem) {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.id = 'googlePayScript'
      script.src = 'https://pay.google.com/gp/p/js/pay.js'
      document.body.appendChild(script)
    }
  }

  const initGooglePayButtons = async (data: PaymentMethodSetupData) => {
    if (shippingAddress && data.billingAddress) {
      await initGooglePayScript()
      await createBraintreeGooglePay(data)
    }
  }

  const createBraintreeGooglePayInstance = async (data: PaymentMethodSetupData) => {
    const { btClientInstance } = await createBraintreeClientInstance()
    const googlePayPaymentsClient = new google.payments.api.PaymentsClient({
      environment: `${sfccEnv === 'production' ? 'PRODUCTION' : 'TEST'}`,
      merchantInfo: {
        merchantId: googlePayMerchantId ?? undefined,
        merchantName: googlePayMerchantName ?? undefined
      }
    });

    const googlePayInstance = await googlePayment.create({
      client: btClientInstance, // From braintree.client.create, see below for full example
      googlePayVersion: 2,
      googleMerchantId: googlePayMerchantId
    })

    const dataCollectorInstance = await dataCollector.create({
      // @ts-ignore: Property _client does not exist on object. Docs only document getting the client via
      // the callback method. As we use promises the result is stored as _client.
      client: googlePayInstance._client
    })
    const fraudData = dataCollectorInstance.deviceData

    return { googlePayPaymentsClient, googlePayInstance, fraudData }
  }

  const triggerGooglePay = async (data: GooglePayArgs) => {
    const { gpayInstance, googlePayPaymentsClient, submitOrderProcess, fraudData } = data
    const billingAddress = data.getBillingAddress?.() ?? data.billingAddress
    const amountToPay = data.amountToPay || orderStore.amountToPay
    const paymentDataRequest = await gpayInstance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: currency,
        totalPriceStatus: 'FINAL',
        totalPrice: amountToPay.toFixed(2)
      }
    });
    const cardPaymentMethod = (await paymentDataRequest).allowedPaymentMethods[0];
    cardPaymentMethod.parameters.billingAddressRequired = true;
    cardPaymentMethod.parameters.billingAddressParameters = {
      format: 'FULL'
    }
    googlePayPaymentsClient.loadPaymentData(paymentDataRequest).then(async function (paymentData) {
      await gpayInstance.parseResponse(paymentData, async function (err, result) {
        if (err) {
          orderStore.setIsPaymentLoading(false)
          throw new Error(err.name)
        }

        try {
          if (result.nonce && billingAddress) {
            orderStore.setIsPaymentLoading(true)
            if (result.details.isNetworkTokenized === false) {
              try {
                handleThreeDSAuthentication({
                  nonce: result.nonce,
                  amount: amountToPay,
                  bin: result.details.bin,
                  billingAddress: billingAddress,
                  submit: (payload) => {
                    submitOrderProcess({ token: payload.nonce, cardType: CARD_TYPES.GOOGLE_PAY, billingAddress, paymentMethodId: PAYMENT_METHOD_IDS.GOOGLE_PAY, fraudDeviceData: fraudData, accountEmail: billingAddress.email ?? '' })
                  }
                })

              } catch (error) {
                orderStore.setIsPaymentLoading(false)
                console.error(error)
              }
            } else {
              await submitOrderProcess({ token: result.nonce, cardType: CARD_TYPES.GOOGLE_PAY, billingAddress, paymentMethodId: PAYMENT_METHOD_IDS.GOOGLE_PAY, fraudDeviceData: fraudData, accountEmail: billingAddress.email ?? '' });
            }
          }
        } catch (error) {
          orderStore.setIsPaymentLoading(false)
          throw new Error(error)
        }
      })
    })
  }

  const createBraintreeGooglePay = async (data: PaymentMethodSetupData) => {
    // Render once
    if (!hasGooglePayButtonRendered) {
      setHasGooglePayButtonRendered(false)
      try {
        const { googlePayPaymentsClient, googlePayInstance, fraudData } = await createBraintreeGooglePayInstance(data)
        const gpayInstance = (await googlePayInstance)

        if (!googlePayInstance) {
          throw Error('Error connecting to braintree')
        }

        googlePayPaymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: (await gpayInstance.createPaymentDataRequest()).allowedPaymentMethods,
        }).then(async function (isReadyToPay) {
          if (isReadyToPay.result) {
            const button = await googlePayPaymentsClient.createButton({
              buttonType: 'pay',
              buttonColor: 'black',
              buttonSizeMode: 'fill',
              onClick: async () => {
                try {
                  if (data.isBillingAddressValid && !data.isBillingAddressValid()) {
                    orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.BILLING_ADDRESS_INVALID)
                    return
                  }
                  const isValid = await reviewBasket()
                  if (!isValid) return
                  await triggerGooglePay({ gpayInstance, googlePayPaymentsClient, fraudData, ...data })
                } catch (error) {
                  console.error(error)
                  orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.GOOGLE_PAY_INITIALIZATION_ERROR)
                }
              }
            })

            const button2 = await googlePayPaymentsClient.createButton({
              buttonType: 'pay',
              buttonColor: 'black',
              buttonSizeMode: 'fill',
              onClick: async () => {
                try {
                  if (data.isBillingAddressValid && !data.isBillingAddressValid()) {
                    orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.BILLING_ADDRESS_INVALID)
                    return
                  }
                  const isValid = await reviewBasket()
                  if (!isValid) return
                  await triggerGooglePay({ gpayInstance, googlePayPaymentsClient, fraudData, ...data })
                } catch (error) {
                  console.error(error)
                  orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.GOOGLE_PAY_INITIALIZATION_ERROR)
                }
              }
            })

            const container1 = document.getElementById('GooglePay-container-1')

            if (container1) {
              // Clear existing buttons if switching between payments until refactored
              container1.innerHTML = ''
              container1.appendChild(button);
            }
            const container2 = document.getElementById('GooglePay-container-2')
            if (container2) {
              // Clear existing buttons if switching between payments until refactored
              container2.innerHTML = ''
              container2.appendChild(button2);
            }
            setHasGooglePayButtonRendered(true)
          }
        })
      } catch (error) {
        orderStore.setIsPaymentLoading(false)
        console.error(error)
      }
    }
  }

  return {
    initGooglePayButtons,
    hasGooglePayButtonRendered
  }
}
